// Usage limits of a CLI as this PC's agent last fetched them: one bar per
// window (5 hours, the week, per model), how much is used, when it resets, and
// a mark of how far the window has run. Used ahead of the time that has passed
// is drawn in the danger color.
import { m } from "@trameria/i18n";
import type { RateLimits, RateLimitWindow } from "@trameria/shared-agent";
import { useFormat } from "../me/profile";
import { dayDistance } from "../time/format";

function windowName(w: RateLimitWindow): string {
  const hours = Math.round(w.windowSeconds / 3600);
  if (w.kind === "model") return m.agents_limit_model({ model: w.model ?? "" });
  if (hours === 168) return m.agents_limit_week();
  if (hours % 24 === 0) return m.agents_limit_days({ n: hours / 24 });
  return m.agents_limit_hours({ n: hours });
}

/** One window's bar as data: what the web draws and a host's panel shows. */
export interface LimitBar {
  key: string;
  label: string;
  /** When it resets, written out. */
  detail: string | null;
  usedPercent: number;
  /** How far the window has run, 0 to 100; null when its end is unknown. */
  elapsed: number | null;
  /** Used ahead of the time that has passed. */
  ahead: boolean;
}

/** The windows worth a bar (a model's only once used), as data. */
export function limitBars(
  limits: RateLimits,
  format: ReturnType<typeof useFormat>,
  now = Date.now(),
): LimitBar[] {
  return limits.windows
    .filter((w) => w.kind !== "model" || w.usedPercent > 0)
    .map((w) => {
      const resets = w.resetsAt ? Date.parse(w.resetsAt) : null;
      const elapsed =
        resets === null
          ? null
          : Math.min(
              100,
              Math.max(
                0,
                100 - ((resets - now) / (w.windowSeconds * 1000)) * 100,
              ),
            );
      const when =
        w.resetsAt === null
          ? null
          : dayDistance(w.resetsAt, { timeZone: format.timeZone }) === 0
            ? format.time(w.resetsAt)
            : format.dateTime(w.resetsAt);
      return {
        key: `${w.kind}:${w.model ?? ""}:${w.windowSeconds}`,
        label: windowName(w),
        detail: when ? m.agents_limit_resets({ when }) : null,
        usedPercent: w.usedPercent,
        elapsed,
        ahead: elapsed !== null && w.usedPercent > elapsed + 5,
      };
    });
}

/** The quiet line in place of the bars, or under them; null for none. */
export function limitsNote(
  limits: RateLimits | null,
  off: boolean,
  format: ReturnType<typeof useFormat>,
): string | null {
  if (off) return m.agents_limit_off();
  if (!limits || limits.windows.length === 0) return m.agents_limit_none();
  return limits.error
    ? m.agents_limit_error({
        error: limits.error,
        at: format.dateTime(limits.at),
      })
    : null;
}

export function RateLimitBars({
  limits,
  off = false,
}: {
  limits: RateLimits | null;
  /** The device's setting turned fetching off: say so instead of "not yet". */
  off?: boolean;
}) {
  const format = useFormat();
  const note = limitsNote(limits, off, format);
  if (!limits || off || limits.windows.length === 0)
    return <p className="text-[12px] text-faint">{note}</p>;
  return (
    <div className="flex flex-col gap-2.5">
      {limitBars(limits, format).map((w) => {
        const ahead = w.ahead;
        const elapsed = w.elapsed;
        return (
          <div key={w.key}>
            <div className="flex items-baseline gap-2 text-[12px]">
              <span className="text-body">{w.label}</span>
              {w.detail && <span className="text-faint">{w.detail}</span>}
              <span
                className={`ml-auto tabular-nums ${ahead ? "text-danger" : "text-muted"}`}
              >
                {Math.round(w.usedPercent)}%
              </span>
            </div>
            <div className="relative mt-1 h-1.5 overflow-hidden rounded-full bg-border">
              <div
                className={`h-full rounded-full ${ahead ? "bg-danger" : "bg-muted"}`}
                style={{ width: `${Math.min(100, w.usedPercent)}%` }}
              />
              {elapsed !== null && (
                <div
                  aria-hidden="true"
                  className="absolute top-0 h-full w-[2px] bg-fg"
                  style={{ left: `${elapsed}%` }}
                />
              )}
            </div>
          </div>
        );
      })}
      {note && <p className="text-[11.5px] text-faint">{note}</p>}
    </div>
  );
}
